import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Typography,
  Divider,
  IconButton,
  InputAdornment,
  Alert,
  CircularProgress,
} from "@mui/material";
import "../../../src/index.css";
import { ThemeProvider } from "@mui/material/styles";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import avatarImg from "../../assets/loginandsignup/OBJECTS.png";
import logoImg from "../../assets/SocialRipplelogo_J.png";
import "./Signup.css";
import theme from "../../styles/theme";
import { Link } from "react-router-dom";
import authService from "../../services/authService";
import { formTypes } from "../../shared/constants";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { Auth } from "../../contexts/AuthContext";
import { GoogleLogin } from "@react-oauth/google";
import { useMsal } from "@azure/msal-react";
import { toast } from "react-toastify";
import { isOnCorrectSubdomain, redirectToOrganizationSubdomain } from "../../shared/subdomainRedirect";

const isPayoneerSubdomain = () => {
  const hostname = window.location.hostname.toLowerCase();
  return hostname.includes("payoneer") || hostname.includes("payoneers");
};

const getSsoButtonText = () => {
  const hostname = window.location.hostname.toLowerCase();
  if (hostname.includes("payoneers")) {
    return "Sign-In with Payoneers SSO";
  }
  if (hostname.includes("payoneer")) {
    return "Sign-In with Payoneer SSO";
  }
  return "Continue with SSO";
};

const isOrgSubdomain = () => {
  const hostname = window.location.hostname.toLowerCase();
  if (isPayoneerSubdomain()) return true;
  if (hostname.includes(".socialripple.ai")) {
    const subdomain = hostname.split(".")[0];
    return subdomain !== "app" && subdomain !== "dashboard" && subdomain !== "api" && subdomain !== "staging" && subdomain !== "www";
  }
  if (hostname.endsWith(".localhost") && hostname !== "localhost") {
    return true;
  }
  return false;
};

const parseAuthError = (error) => {
  if (error?.errorCode === "user_cancelled" || error?.message?.toLowerCase()?.includes("user cancelled")) {
    return "";
  }

  const backendCode = error?.response?.data?.code;
  const backendMessage = error?.response?.data?.message;

  if (backendCode) {
    switch (backendCode) {
      case "USMG_401":
        if (backendMessage && backendMessage.toLowerCase().includes("token")) {
          return "Session/token error. Please sign in again.";
        }
        return "Invalid credentials. Please check your email and password.";
      case "USMG_404":
        return "Account not found. Please register or contact support.";
      case "USMG_423":
        return "Account is temporarily locked due to too many failed attempts. Please try again later.";
      case "USMG_409":
        return "Account already exists.";
      case "USMG_400":
        return backendMessage || "Bad request. Please check the input parameters.";
      default:
        break;
    }
  }
  return backendMessage || error?.message || "An unexpected error occurred during authentication.";
};

const SignUpPage = ({ formType }) => {
  const { setUser } = Auth();
  const navigate = useNavigate();
  const location = useLocation();
  // Destination URL preserved by PrivateRoute when redirecting unauthenticated users to /sign-in
  const intendedDestination = location.state?.from;
  const params = new URLSearchParams(location.search);
  const invitationToken = params.get("token");
  const isInviteSignup = formType === formTypes.SIGNUP && Boolean(invitationToken);

  // After a successful login, land the user on their organization's own subdomain
  // (if one is configured) instead of the host they logged in from. `target` may be
  // a plain path string or a React Router location object (e.g. `intendedDestination`).
  const goToPath = (session, target, navigateOptions) => {
    if (session.organizationSubdomain && !isOnCorrectSubdomain(session.organizationSubdomain)) {
      const path = typeof target === "string" ? target : `${target?.pathname || "/"}${target?.search || ""}`;
      redirectToOrganizationSubdomain(session.organizationSubdomain, path);
      return;
    }
    navigate(target, navigateOptions);
  };
  const [loading, setLoading] = useState(false);
  const [, setInviteLoading] = useState(Boolean(invitationToken));
  const [inviteDataLoaded, setInviteDataLoaded] = useState(false);
  const [resetToken, setResetToken] = useState("");
  const [recoveryStep, setRecoveryStep] = useState(1);
  const [isSsoMode, setIsSsoMode] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    mobileNumber: "",
    password: "",
    confirmPassword: "",
    otp: "",
  });

  const [errors, setErrors] = useState({});
  const [alert, setAlert] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const GOOGLE_PROVIDER_ID = 1;
  const MICROSOFT_PROVIDER_ID = 2;

  useEffect(() => {
    if (isPayoneerSubdomain()) {
      if (formType === formTypes.SIGNUP) {
        navigate("/sign-in", { replace: true });
        return;
      }
      if (formType === formTypes.SIGNIN) {
        setIsSsoMode(true);
      }
    } else {
      setIsSsoMode(false);
    }
    setAlert("");
  }, [formType, navigate]);

  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  const handleResendOtp = async () => {
    if (!formData.email) return;
    setLoading(true);
    setAlert("");
    try {
      const response = await authService.forgotPassword({ email: formData.email });
      if (response?.status === 200 || response?.data?.status === true) {
        const token = response?.data?.data?.resetToken || response?.data?.resetToken;
        if (token) {
          setResetToken(token);
        }
        toast.success("A new OTP has been sent to your email.");
      }
    } catch (error) {
      setAlert(parseAuthError(error));
    } finally {
      setLoading(false);
    }
  };

  // Pre-fill form from invitation data when signup link has a token
  useEffect(() => {
    if (invitationToken && isInviteSignup) {
      setInviteLoading(true);
      authService.getInviteInfo(invitationToken).then((response) => {
        if (response?.data?.status === true) {
          setFormData((prev) => ({
            ...prev,
            firstName: response.data.firstName || "",
            lastName: response.data.lastName || "",
            email: response.data.email || "",
            mobileNumber: response.data.phone || "",
          }));
          setInviteDataLoaded(true);
        }
      }).catch(() => { }).finally(() => setInviteLoading(false));
    }
  }, [invitationToken, isInviteSignup]);

  const { instance } = useMsal();

  const handleSsoSuccess = async (providerId, ssoToken) => {
    const payload = {
      providerId,
      ssoToken,
    };

    if (isInviteSignup) {
      payload.invitationToken = invitationToken;
    }

    const backendResponse =
      isInviteSignup
        ? await authService.signupWithSso(payload)
        : formType === formTypes.SIGNUP
          ? await authService.registerWithSso(payload)
          : await authService.loginWithSso(payload);

    const session = authService.extractSession(backendResponse);
    const primaryRole = session?.role?.[0];

    if (!session?.token) {
      throw new Error(
        backendResponse?.data?.message || "SSO authentication did not return an access token."
      );
    }

    setUser(session);
    toast.success(
      formType === formTypes.SIGNUP
        ? "Welcome! You are now signed in with SSO."
        : "Welcome back! You are now signed in.",
      {
        style: {
          background: "#0047ab",
          color: "#fff",
        },
      }
    );

    if (!session.organizationId) {
      goToPath(session, "/organization-setup");
      return;
    }

    if (primaryRole === "ROLE_ADMIN") {
      goToPath(session, "/admin");
      return;
    }

    if (intendedDestination && intendedDestination.pathname !== "/sign-in") {
      goToPath(session, intendedDestination, { replace: true });
      return;
    }
    goToPath(session, "/employees/timeline");
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      if (!credentialResponse?.credential) {
        throw new Error("Google did not return an ID token.");
      }

      await handleSsoSuccess(GOOGLE_PROVIDER_ID, credentialResponse.credential);
    } catch (error) {
      console.error("Google login failed", error);
      setAlert(parseAuthError(error));
    }
  };

  const handleMicrosoftLogin = async (loginHint) => {
    try {
      const loginRequest = {
        scopes: ["openid", "profile", "email", "User.Read"],
        prompt: loginHint ? undefined : "select_account",
      };
      if (loginHint && typeof loginHint === "string" && loginHint.includes("@")) {
        loginRequest.loginHint = loginHint.trim();
      }
      const loginResponse = await instance.loginPopup(loginRequest);

      if (!loginResponse?.idToken) {
        throw new Error("Microsoft did not return an ID token.");
      }

      instance.setActiveAccount(loginResponse.account || null);
      await handleSsoSuccess(MICROSOFT_PROVIDER_ID, loginResponse.idToken);
    } catch (error) {
      console.error("Microsoft login failed", error);
      setAlert(parseAuthError(error));
    }
  };

  const toggleConfirmPasswordVisibility = () =>
    setShowConfirmPassword(!showConfirmPassword);
  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validatePhone = (phone) => /^\d{10}$/.test(phone);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let errorMsg = "";

    if (!value.trim()) {
      errorMsg = `${name.replace(/([A-Z])/g, " $1")} is required`;
    } else if (name === "email" && !validateEmail(value)) {
      errorMsg = "Invalid email format";
    } else if (name === "otp" && !/^\d{6}$/.test(value.trim())) {
      errorMsg = "OTP must be a 6-digit number";
    } else if (name === "mobileNumber" && !validatePhone(value)) {
      errorMsg = "Invalid phone number (10 digits required)";
    } else if (name === "password") {
      const strongPasswordRegex =
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>]).{8,}$/;

      if (!strongPasswordRegex.test(value) && formType !== "signIn") {
        errorMsg =
          "Password must be at least 8 characters long and include an uppercase letter, a lowercase letter, a number, and a special character";
      }
    } else if (name === "confirmPassword" && value !== formData.password) {
      errorMsg = "Passwords do not match";
    }

    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: errorMsg });
    setAlert("");
  };

  const validateForm = () => {
    let validationErrors = {};
    if (formType === formTypes.SIGNUP) {
      if (!formData.firstName) validationErrors.firstName = true;
      if (!formData.lastName) validationErrors.lastName = true;
      if (!formData.email) validationErrors.email = true;
      if (!formData.mobileNumber) validationErrors.mobileNumber = true;
      if (!formData.password) validationErrors.password = true;
      if (formData.password !== formData.confirmPassword || !formData.password)
        validationErrors.confirmPassword = true;
    } else if (formType === formTypes.SIGNIN) {
      if (!formData.email) validationErrors.email = true;
      if (!isSsoMode && !formData.password) validationErrors.password = true;
    } else if (formType === formTypes.PASSWORD_RECOVERY) {
      if (recoveryStep === 1) {
        if (!formData.email) validationErrors.email = true;
      } else {
        if (!formData.otp) validationErrors.otp = true;
        if (!formData.password) validationErrors.password = true;
        if (formData.password !== formData.confirmPassword || !formData.password)
          validationErrors.confirmPassword = true;
      }
    } else if (formType === formTypes.UPDATE_PASSWORD) {
      if (!formData.otp) validationErrors.otp = true;
      if (!formData.password) validationErrors.password = true;
      if (formData.password !== formData.confirmPassword || !formData.password)
        validationErrors.confirmPassword = true;
    }

    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      setAlert("Please fill all fields to continue");
    }

    return Object.keys(validationErrors).length === 0;
  };

  const handleSsoClick = () => {
    setIsSsoMode(true);
    setAlert("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formType === formTypes.SIGNIN && isSsoMode) {
      if (!formData.email || !validateEmail(formData.email)) {
        setErrors((prev) => ({ ...prev, email: "Please enter a valid work email" }));
        setAlert("Please enter a valid work email to continue with SSO");
        return;
      }
      setLoading(true);
      setAlert("");
      try {
        let tenantId = "";
        const hostname = window.location.hostname.toLowerCase();
        if (hostname.includes(".socialripple.ai")) {
          const subdomain = hostname.split(".")[0];
          if (subdomain !== "app" && subdomain !== "dashboard" && subdomain !== "api" && subdomain !== "staging") {
            tenantId = subdomain;
          }
        }
        if (!tenantId && formData.email && formData.email.includes("@")) {
          const domain = formData.email.split("@")[1].toLowerCase();
          tenantId = domain.split(".")[0];
        }

        if (tenantId) {
          try {
            const samlRes = await authService.getSamlLoginUrl(tenantId, window.location.origin + "/employees/timeline", false, formData.email);
            const samlRedirectUrl = samlRes?.samlRedirectUrl || samlRes?.data?.samlRedirectUrl;
            if (samlRedirectUrl) {
              window.location.href = samlRedirectUrl;
              return;
            }
          } catch (samlErr) {
            console.log("SAML SSO initiation not active for tenant, fallback to MSAL", samlErr);
          }
        }

        await handleMicrosoftLogin(formData.email);
      } catch (err) {
        console.error("SSO Login Error:", err);
        setAlert(parseAuthError(err));
      } finally {
        setLoading(false);
      }
      return;
    }

    if (!validateForm()) return;
    setAlert("");
    setLoading(true);
    try {
      const captchaToken = null;
      let response;
      switch (formType) {
        case formTypes.SIGNUP:
          if (isInviteSignup) {
            const payload = {
              ...formData,
              inviteToken: invitationToken,
              captchaToken,
            };
            response = await authService.signUp(payload);
          } else {
            response = await authService.register({
              ...formData,
              captchaToken,
            });
          }
          if (response?.status === 200 || response?.status === 201) {
            toast.success(
              isInviteSignup
                ? "Welcome! You are now signed up. please login"
                : "Account created successfully. Sign in to continue organization setup."
            );
            navigate("/sign-in");
          }
          break;
        case formTypes.SIGNIN:
          response = await authService.signIn(
            formData.email,
            formData.password,
            captchaToken
          );
          if (response?.status === 200) {
            const session = authService.extractSession(response);
            if (!session.token) {
              throw new Error("Authentication token missing from login response");
            }
            setUser(session);
            toast.success("Welcome back! You are now signed in.", {
              style: {
                background: "#0047ab",
                color: "#fff",
              },
            });
            if (!session.organizationId) {
              goToPath(session, "/organization-setup");
            } else if (session?.role?.[0] === "ROLE_ADMIN") {
              goToPath(session, "/admin");
            } else if (intendedDestination && intendedDestination.pathname !== "/sign-in") {
              goToPath(session, intendedDestination, { replace: true });
            } else {
              goToPath(session, "/employees/timeline");
            }
          }
          break;
        case formTypes.PASSWORD_RECOVERY:
          if (recoveryStep === 1) {
            response = await authService.forgotPassword({ email: formData.email });
            if (response?.status === 200 || response?.data?.status === true) {
              const token = response?.data?.data?.resetToken || response?.data?.resetToken;
              if (token) {
                setResetToken(token);
              }
              setRecoveryStep(2);
              toast.success("OTP sent to your email. Please enter the verification code below.");
            }
          } else {
            response = await authService.resetPassword({
              resetToken: resetToken || invitationToken || "",
              otp: formData.otp,
              newPassword: formData.password,
              confirmNewPassword: formData.confirmPassword,
            });
            if (response?.status === 200 || response?.data?.status === true) {
              toast.success("Password updated successfully. Please sign in with your new password.");
              navigate("/sign-in");
            }
          }
          break;
        case formTypes.UPDATE_PASSWORD:
          response = await authService.resetPassword({
            resetToken: resetToken || invitationToken || "",
            otp: formData.otp,
            newPassword: formData.password,
            confirmNewPassword: formData.confirmPassword,
          });
          if (response?.status === 200 || response?.data?.status === true) {
            toast.success("Password updated successfully. Please sign in with your new password.");
            navigate("/sign-in");
          }
          break;
        default:
          return;
      }
    } catch (error) {
      console.error(error);
      const errorMessage = parseAuthError(error);
      setAlert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="root-container">
      <div className="logo-container">
        <img className="new_ico" src={isPayoneerSubdomain() ? "/payoneer_logo.png" : logoImg} alt="Logo" />
      </div>
      <Box
        display={"flex"}
        justifyContent={"space-between"}
        alignItems={"center"}
        flexDirection={{ xs: "column", md: "row" }}
        gap={{ xs: 3, md: 0 }}
        sx={{
          width: "100%",
          minHeight: { xs: "auto", md: "80vh" },
          px: { md: 8 },
          py: { xs: 4, md: 0 },
        }}
      >
        <Box
          sx={{
            width: { xs: "100%", sm: "80%", md: "50%", lg: "55%" },
            display: { xs: "none", md: "flex" },
            justifyContent: "center",
          }}
          className="image-container"
        >
          <img
            src={avatarImg}
            alt="Avatar"
            style={{
              width: "80%",
              maxWidth: "500px",
            }}
          />
        </Box>
        <Box
          sx={{
            width: { xs: "100%", md: "45%", lg: "40%" },
            display: "flex",
            justifyContent: { xs: "center", md: "flex-end" },
            alignItems: "center",
            mt: { xs: 2, md: 0 },
          }}
        >
          <Box className="form-container">
            <Typography className="form-header" sx={{ cursor: "default !important", mb: isSsoMode ? 1 : 2 }}>
              {formType === formTypes.SIGNUP
                ? isInviteSignup
                  ? "Accept Invite"
                  : "Sign Up"
                : formType === formTypes.SIGNIN
                  ? isSsoMode
                    ? "Sign In with SSO"
                    : "Sign In"
                  : formType === formTypes.PASSWORD_RECOVERY
                    ? (recoveryStep === 1 ? "Password Recovery" : "Reset Password")
                    : "Update Password"}
            </Typography>
            {formType === formTypes.SIGNIN && isSsoMode && (
              <Typography
                variant="body2"
                sx={{
                  color: "#4E4C59",
                  fontSize: "13px",
                  lineHeight: "1.5",
                  mb: 2.5,
                  textAlign: "left",
                  wordBreak: "break-word",
                  whiteSpace: "normal",
                  cursor: "default",
                }}
              >
                Enter your work email address to log in with your organization's.
              </Typography>
            )}
            {formType === formTypes.PASSWORD_RECOVERY && recoveryStep === 2 && (
              <Typography variant="body2" sx={{ color: "#4E4C59", fontSize: "12px", mb: 2, textAlign: "center" }}>
                Enter the 6-digit verification code sent to <strong>{formData.email}</strong>
              </Typography>
            )}
            {alert && (
              <Alert
                severity="warning"
                style={{
                  marginBottom: "5px",
                  fontSize: "12px",
                  color: "#FFFFFF",
                  backgroundColor: "#DD3523",
                  border: "1px solid #DD3523",
                }}
              >
                {alert}
              </Alert>
            )}
            <ThemeProvider theme={theme}>
              {formType === formTypes.SIGNUP ? (
                <>
                  <TextField
                    placeholder="First Name"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    error={!!errors.firstName}
                    helperText={errors.firstName}
                    fullWidth
                    variant="outlined"
                    sx={{
                      "& .MuiInputBase-root": {
                        height: "40px",
                      },
                      "& .MuiOutlinedInput-input": {
                        padding: "5px 14px !important"
                      }
                    }}
                  />
                  <TextField
                    placeholder="Last Name"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    error={!!errors.lastName}
                    helperText={errors.lastName}
                    fullWidth
                    variant="outlined"
                    sx={{
                      "& .MuiInputBase-root": {
                        height: "40px",
                      },
                      "& .MuiOutlinedInput-input": {
                        padding: "5px 14px !important"
                      }
                    }}
                  />
                </>
              ) : null}
              {formType === formTypes.SIGNUP ||
                formType === formTypes.SIGNIN ||
                (formType === formTypes.PASSWORD_RECOVERY && recoveryStep === 1) ? (
                <>
                  <TextField
                    placeholder={formType === formTypes.SIGNIN && isSsoMode ? "Work Email" : "Email"}
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    onKeyDown={(e) => { if (e.key === "Enter") handleSubmit(e); }}
                    error={!!errors.email}
                    helperText={errors.email}
                    fullWidth
                    variant="outlined"
                    disabled={inviteDataLoaded}
                    type="email"
                    sx={{
                      "& .MuiInputBase-root": {
                        height: "40px",
                      },
                      "& .MuiOutlinedInput-input": {
                        padding: "5px 14px !important"
                      }
                    }}
                  />
                </>
              ) : null}
              {(formType === formTypes.UPDATE_PASSWORD ||
                (formType === formTypes.PASSWORD_RECOVERY && recoveryStep === 2)) ? (
                <TextField
                  placeholder="Enter 6-Digit OTP"
                  name="otp"
                  value={formData.otp}
                  onChange={handleChange}
                  onKeyDown={(e) => { if (e.key === "Enter") handleSubmit(e); }}
                  error={!!errors.otp}
                  helperText={errors.otp}
                  fullWidth
                  variant="outlined"
                  inputProps={{ maxLength: 6 }}
                  sx={{
                    "& .MuiInputBase-root": {
                      height: "40px",
                    },
                    "& .MuiOutlinedInput-input": {
                      padding: "5px 14px !important"
                    }
                  }}
                />
              ) : null}
              {formType === formTypes.SIGNUP ? (
                <>
                  <Box className="phoneInput">
                    <PhoneInput
                      name="mobileNumber"
                      error={!!errors.mobileNumber}
                      helperText={errors.mobileNumber}
                      international
                      defaultCountry="IN"
                      value={formData.mobileNumber}
                      placeholder="Mobile Number"
                      onChange={(value) => {
                        setFormData({ ...formData, mobileNumber: value });
                        setErrors({ ...errors, mobileNumber: false });
                        setAlert("");
                      }}
                      className="phone-input"
                      disableCountryCode={false}
                    />
                  </Box>
                </>
              ) : null}

              {(formType === formTypes.SIGNIN && !isSsoMode) ||
                formType === formTypes.SIGNUP ||
                formType === formTypes.UPDATE_PASSWORD ||
                (formType === formTypes.PASSWORD_RECOVERY && recoveryStep === 2) ? (
                <>
                  <TextField
                    placeholder={formType === formTypes.SIGNIN ? "Enter Password" : "New Password"}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    onKeyDown={(e) => { if (e.key === "Enter") handleSubmit(e); }}
                    error={!!errors.password}
                    helperText={errors.password}
                    fullWidth
                    variant="outlined"
                    type={showPassword ? "text" : "password"}
                    onFocus={() => setFocusedField('password')}
                    onBlur={() => setFocusedField(null)}
                    sx={{
                      "& .MuiInputBase-root": {
                        height: "40px",
                      },
                      "& .MuiOutlinedInput-input": {
                        padding: "5px 14px !important"
                      }
                    }}
                    InputProps={{
                      endAdornment: focusedField === 'password' ? (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={togglePasswordVisibility}
                            onMouseDown={(e) => e.preventDefault()}
                            edge="end"
                          >
                            {showPassword ? (
                              <Visibility
                                sx={{ color: "#95919D", fontSize: "14px" }}
                              />
                            ) : (
                              <VisibilityOff
                                sx={{ color: "#95919D", fontSize: "14px" }}
                              />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                    }}
                  />
                  {formType === formTypes.SIGNIN && (
                    <Typography
                      variant="body2"
                      align="right"
                      style={{
                        marginTop: "0.5em",
                        fontSize: "12px",
                        cursor: "pointer",
                        color: "#2D76DC",
                      }}
                    >
                      <Link
                        to="/password-recovery"
                        className="navigation-texts"
                      >
                        Forgot password?
                      </Link>
                    </Typography>
                  )}
                </>
              ) : null}
              {formType === formTypes.SIGNUP ||
                formType === formTypes.UPDATE_PASSWORD ||
                (formType === formTypes.PASSWORD_RECOVERY && recoveryStep === 2) ? (
                <>
                  <TextField
                    placeholder="Confirm Password"
                    name="confirmPassword"

                    value={formData.confirmPassword}
                    sx={{
                      "& .MuiInputBase-root": {
                        height: "40px",
                      },
                      "& .MuiOutlinedInput-input": {
                        padding: "5px 14px !important"
                      }
                    }}
                    onChange={handleChange}
                    error={!!errors.confirmPassword}
                    helperText={errors.confirmPassword}
                    fullWidth
                    variant="outlined"
                    type={showConfirmPassword ? "text" : "password"}
                    onFocus={() => setFocusedField('confirmPassword')}
                    onBlur={() => setFocusedField(null)}
                    InputProps={{
                      endAdornment: focusedField === 'confirmPassword' ? (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={toggleConfirmPasswordVisibility}
                            onMouseDown={(e) => e.preventDefault()}
                            edge="end"
                          >
                            {showConfirmPassword ? (
                              <Visibility
                                sx={{ color: "#95919D", fontSize: "14px" }}
                              />
                            ) : (
                              <VisibilityOff
                                sx={{ color: "#95919D", fontSize: "14px" }}
                              />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                    }}
                  />
                </>
              ) : null}
            </ThemeProvider>
            {formType === formTypes.PASSWORD_RECOVERY && recoveryStep === 2 && (
              <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 1, px: 0.5 }}>
                <Typography
                  variant="body2"
                  sx={{ fontSize: "12px", cursor: "pointer", color: "#2D76DC" }}
                  onClick={() => { setRecoveryStep(1); setAlert(""); }}
                >
                  ← Change Email
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontSize: "12px", cursor: "pointer", color: "#2D76DC" }}
                  onClick={handleResendOtp}
                >
                  Resend OTP
                </Typography>
              </Box>
            )}
            {loading ? (
              <Box
                display={"flex"}
                justifyContent={"center"}
                alignItems="center"
              >
                <CircularProgress sx={{ ml: 2 }} />
              </Box>
            ) : (
              <Button
                variant="contained"
                color="primary"
                fullWidth
                sx={{
                  marginTop: { md: "1.5rem", xs: "1rem", lg: "1.5rem" },
                  textTransform: "none",
                  borderRadius: "6px",
                  fontWeight: "bold",
                  fontSize: "12px",
                  backgroundColor: "#0047AB",
                  height: "45px",
                }}
                onClick={handleSubmit}
              >
                {formType === formTypes.SIGNIN
                  ? (isSsoMode ? getSsoButtonText() : "Sign In")
                  : formType === formTypes.SIGNUP
                    ? isInviteSignup
                      ? "Accept Invite"
                      : "Sign Up"
                    : formType === formTypes.PASSWORD_RECOVERY
                      ? (recoveryStep === 1 ? "Send OTP" : "Reset Password")
                      : "Update Password"}
              </Button>
            )}
            {/* Social Login Buttons (Google, Microsoft) - Commented Out */}
            {/*
            {formType === formTypes.SIGNUP || formType === formTypes.SIGNIN ? (
              <>
                <Divider
                  style={{
                    margin: "10px 0",
                    color: "#4E4C59",
                    borderColor: "#9B9B9B",
                  }}
                >
                  or
                </Divider>
                <Box className="SocialLoginContainer">
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      position: "relative",
                      width: "30px",
                      height: "30px",
                      borderRadius: "100%",
                      border: "1px solid #dadce0",

                      cursor: "pointer",
                      transition: "background-color 0.2s ease",
                      "&:hover": {
                        backgroundColor: "#f1f3f4",
                      },
                      "& > div": {
                        lineHeight: 0,
                      },
                    }}
                  >
                    <IconButton
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        zIndex: 1,
                      }}
                    >
                      <img
                        src="/googl-icon.svg"
                        alt="Google"
                        style={{ width: "14px", height: "14px" }}
                      />
                    </IconButton>
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        opacity: 0,
                        zIndex: 2,
                        cursor: "pointer",
                      }}
                    >
                      <GoogleLogin
                        onSuccess={handleGoogleSuccess}
                        onError={() => setAlert("Google sign-in failed")}
                        type="icon"
                        shape="circle"
                        theme="outline"
                        size="medium"
                        text="signin_with"
                        width="40"
                      />
                    </Box>
                  </Box>
                  <IconButton onClick={() => handleMicrosoftLogin(isSsoMode ? formData.email : undefined)} disabled={loading}>
                    <svg
                      width="17"
                      height="17"
                      viewBox="0 0 17 17"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g clipPath="url(#clip0_602_37682)">
                        <path
                          d="M8.09573 0H-0.000976562V8.09483H8.09573V0Z"
                          fill="#F44336"
                        />
                        <path
                          d="M17.0003 0H8.9541V8.09483H17.0003V0Z"
                          fill="#4CAF50"
                        />
                        <path
                          d="M8.09573 8.9043H-0.000976562V16.9991H8.09573V8.9043Z"
                          fill="#2196F3"
                        />
                        <path
                          d="M17.0003 8.9043H8.9541V16.9991H17.0003V8.9043Z"
                          fill="#FFC107"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_602_37682">
                          <rect width="17" height="17" rx="1" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  </IconButton>
                </Box>
              </>
            ) : null}
            */}
            {formType === formTypes.SIGNIN && !isSsoMode && (
              <>
                <Divider
                  style={{
                    margin: "12px 0",
                    color: "#4E4C59",
                    borderColor: "#D5DEEB",
                    fontSize: "12px",
                  }}
                >
                  or
                </Divider>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleSsoClick}
                  sx={{
                    textTransform: "none",
                    borderRadius: "6px",
                    fontWeight: 600,
                    fontSize: "13px",
                    borderColor: "#D0D7DE",
                    color: "#24292F",
                    height: "40px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      borderColor: "#0047AB",
                      backgroundColor: "#F0F5FF",
                      color: "#0047AB",
                    },
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  </svg>
                  Sign in with SSO
                </Button>
              </>
            )}
            <Typography
              variant="body2"
              align="center"
              style={{ marginTop: "0.85em", fontSize: "12px" }}
            >
              {formType === formTypes.SIGNIN ? (
                isSsoMode ? (
                  !isPayoneerSubdomain() ? (
                    <span
                      onClick={() => {
                        setIsSsoMode(false);
                        setAlert("");
                      }}
                      style={{
                        color: "#2D76DC",
                        cursor: "pointer",
                        fontWeight: 500,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px"
                      }}
                    >
                      ← Back to Sign In
                    </span>
                  ) : null
                ) : (
                  <>
                    Don't have an account?{" "}
                    <Link to="/sign-up" className="navigation-texts">
                      Sign Up
                    </Link>
                  </>
                )
              ) : formType === formTypes.SIGNUP ? (
                <>
                  Already have an account?{" "}
                  <Link to="/sign-in" className="navigation-texts">
                    Sign In
                  </Link>
                </>
              ) : formType === formTypes.PASSWORD_RECOVERY ? (
                <Typography variant="body2" align="center">
                  <Link to="/sign-in" className="navigation-texts typo">
                    Back to Sign In
                  </Link>
                </Typography>
              ) : null}
            </Typography>
          </Box>
        </Box>
      </Box>
    </div >
  );
};

export default SignUpPage;
